import * as React from "react";
import { cn } from "@/shared/lib/utils";

export interface ResponsiveViewWrapperProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Maximum width of the centered shell on desktop viewports. Defaults to 430px. */
  maxMobileWidth?: number;
  /** Maximum height of the shell on desktop viewports. Defaults to 880px. */
  maxMobileHeight?: number;
  /** Whether the wrapper is in landscape mode (expands width to 880px). */
  isLandscape?: boolean;
  /** Whether the shell maintains a fixed device height or hugs content up to maxMobileHeight. Defaults to true. */
  fixedHeight?: boolean;
  /** Whether the interior container automatically enables vertical scrolling. Defaults to true. */
  scrollable?: boolean;
  /** Custom classes for the outer full-screen viewport container. */
  containerClassName?: string;
  /** Custom classes for the inner scrollable/content area. */
  contentClassName?: string;
}

/**
 * Enforces Flutter's mobile-in-browser layout (`ResponsiveViewWrapper.dart`).
 * On desktop or wider screens, it renders a centered mobile device container with
 * the signature brand blue border (`border-2 border-brand`), rounded corners (`rounded-shell`),
 * and light blue viewport canvas (`#E4F2FD`), matching the reference login screen.
 * On mobile screens, it gracefully fills the viewport while preserving the branded shell.
 */
export function ResponsiveViewWrapper({
  maxMobileWidth = 430,
  maxMobileHeight = 880,
  isLandscape = false,
  fixedHeight = true,
  scrollable = true,
  containerClassName,
  contentClassName,
  className,
  children,
  ...props
}: ResponsiveViewWrapperProps) {
  const effectiveMaxWidth = isLandscape ? maxMobileHeight : maxMobileWidth;
  const effectiveMaxHeight = isLandscape ? maxMobileWidth : maxMobileHeight;
  // Reserve the same padding and device safe areas used by the visible-viewport container.
  const shellHeight = `min(${effectiveMaxHeight}px, calc(100dvh - var(--shell-spacing) - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)))`;

  return (
    <div
      className={cn(
        "app-viewport bg-brand-surface flex w-full items-center justify-center select-none",
        containerClassName,
      )}
      {...props}
    >
      {/* Centered mobile device frame resembling a mobile smartphone */}
      <div
        style={{
          maxWidth: `${effectiveMaxWidth}px`,
          maxHeight: shellHeight,
          ...(fixedHeight ? { height: shellHeight } : {}),
        }}
        className={cn(
          "border-brand shadow-shell rounded-shell bg-frame-surface relative flex w-full flex-col overflow-hidden border-2",
          className,
        )}
      >
        {scrollable ? (
          <div
            className={cn(
              "min-h-0 flex-1 overflow-x-hidden overflow-y-auto",
              contentClassName,
            )}
          >
            {children}
          </div>
        ) : (
          children
        )}
      </div>
    </div>
  );
}
